import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useUnit } from 'effector-react';
import { residentialComplexesApi } from '../../api/residentialComplexes';
import AddressAutocompleteField from '../AddressAutocompleteField';
import { $objectKind } from '../../store';
import { complexKindLabel, normalizeComplexKind } from '../../utils/complexKind';

const EditResidentialComplexDialog = ({ open, onClose, complex, onSaved }) => {
  const objectKind = useUnit($objectKind);
  const [form, setForm] = useState({
    name: '',
    address: '',
    latitude: null,
    longitude: null,
    complex_kind: 'mdu',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const kind = normalizeComplexKind(objectKind);
  const kindLabel = complexKindLabel(kind);

  useEffect(() => {
    if (open && complex) {
      setForm({
        name: complex?.name || '',
        address: complex?.address || '',
        latitude: complex?.latitude ?? null,
        longitude: complex?.longitude ?? null,
        complex_kind: kind,
      });
      setError('');
    }
  }, [open, complex, kind]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const applyGeoFromSuggestion = (suggestion) => {
    const lat = Number(suggestion?.data?.geo_lat);
    const lon = Number(suggestion?.data?.geo_lon);
    setForm((prev) => ({
      ...prev,
      latitude: Number.isFinite(lat) ? lat : null,
      longitude: Number.isFinite(lon) ? lon : null,
    }));
  };

  const handleSubmit = async () => {
    const name = (form.name || '').trim();
    const address = (form.address || '').trim();
    if (!name || !address) {
      setError('Укажите название и адрес.');
      return;
    }
    if (!complex?.id) return;
    setSaving(true);
    setError('');
    try {
      await residentialComplexesApi.updateResidentialComplex(String(complex.id), {
        name,
        address,
        latitude: form.latitude,
        longitude: form.longitude,
        complex_kind: kind,
      });
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось сохранить изменения');
    } finally {
      setSaving(false);
    }
  };

  const title = `Редактирование: ${kindLabel}`;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Тип клиента
            </Typography>
            <Chip
              size="small"
              label={kindLabel}
              color={kind === 'settlement' ? 'success' : 'default'}
              variant="outlined"
            />
          </Stack>
          <TextField
            label="Название"
            value={form.name}
            onChange={handleChange('name')}
            autoFocus
            fullWidth
          />
          <AddressAutocompleteField
            label="Адрес"
            value={form.address}
            onValueChange={(value) =>
              setForm((prev) => ({
                ...prev,
                address: value,
                latitude: null,
                longitude: null,
              }))
            }
            onSuggestionSelect={(suggestion) => {
              if (suggestion?.value) {
                setForm((prev) => ({ ...prev, address: suggestion.value }));
              }
              applyGeoFromSuggestion(suggestion);
            }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditResidentialComplexDialog;
